import React, { useState, useEffect } from 'react';
import { Select } from 'antd';
import { useSelector } from 'react-redux';
import { streetsSelector } from '../../store/selectors';
import { setStreets } from '../../store/actions';
import useRequestStreets from '../../../../queries/useRequestStreets';
import { renderOption, getFilteredStreets } from './utils';
import useFilterChange from '../../hooks/useFilterChange';
import { filterDelay } from '../../constants/common';

const ITEMS_PER_PAGE = 100;

const SelectStreets = ({ className }) => {
  const selectedStreets = useSelector(streetsSelector);

  const [value, setValue] = useState(selectedStreets);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [filteredStreets, setFilteredStreets] = useState([]);
  const [displayedStreets, setDisplayedStreets] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const { data: streets, isFetching: isStreetsFetching } = useRequestStreets({
    staleTime: Infinity,
  });

  const change = useFilterChange({ action: setStreets, delay: filterDelay });

  const handleSelectChange = (values) => {
    setValue(values);
    change(values);
    setSearchValue('');
    setDebouncedSearchValue('');
  };

  const handleDropdownVisibleChange = (visible) => {
    if (visible) {
      setSearchValue('');
      setDebouncedSearchValue('');
    }
  };

  const handleScroll = ({ target }) => {
    const { scrollTop, offsetHeight, scrollHeight } = target;

    if (displayedStreets.length >= filteredStreets.length) {
      return;
    }

    if (scrollTop + offsetHeight >= scrollHeight - 50) {
      const nextPage = currentPage + 1;
      const newStreets = filteredStreets.slice(0, nextPage * ITEMS_PER_PAGE);

      setDisplayedStreets(newStreets);
      setCurrentPage(nextPage);
    }
  };

  const handleSearch = (value) => {
    setSearchValue(value);
  };

  useEffect(() => {
    const newFilteredStreets = getFilteredStreets(
      streets,
      debouncedSearchValue
    );
    setFilteredStreets(newFilteredStreets);
    setDisplayedStreets(newFilteredStreets.slice(0, ITEMS_PER_PAGE));
    setCurrentPage(1);
  }, [streets, debouncedSearchValue]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 800);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchValue]);

  useEffect(() => {
    setValue(selectedStreets);
  }, [selectedStreets]);

  return (
    <Select
      onPopupScroll={handleScroll}
      onSearch={handleSearch}
      onChange={handleSelectChange}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      className={className}
      loading={isStreetsFetching}
      mode="multiple"
      defaultActiveFirstOption={false}
      allowClear
      placeholder="Улица"
      value={streets?.length ? value : []}
      optionLabelProp="label"
      filterOption={false}
    >
      {displayedStreets.map((street) => (
        <Select.Option
          key={street.id}
          value={street.id}
          searchtext={street.name}
          label={street.name}
        >
          {renderOption(street)}
        </Select.Option>
      ))}
    </Select>
  );
};

export default SelectStreets;
